import React, { useCallback } from 'react';
import { BattleState, BattleUnitState, AIAction } from '../types/battle';
import { Notification } from '../components/NotificationToast';
import { FloatingText } from '../components/BattleEffects';
import { getComboCallout } from '../utils/battleUtils';
import { playSfx } from '../utils/sound';
import { getBattleSpeed, battleSpeedDivisor } from '../utils/battleSpeed';

type SetBattle = React.Dispatch<React.SetStateAction<BattleState | null>>;
type AddFloatingText = (text: string, type: FloatingText['type'], x: number, y: number) => void;
type SpawnFx = (skill: string, from: { x: number; y: number }, to: { x: number; y: number }) => void;

const TILE_SIZE = 80;
const gridToPixel = (gridX: number, gridY: number) => ({
  x: gridX * TILE_SIZE + TILE_SIZE / 2,
  y: gridY * TILE_SIZE + TILE_SIZE / 2,
});

// Base pacing at 1x. Tightened from the original (750/800/1500/1200/500) per GH-53 -
// combat felt sluggish with several enemies acting in sequence. 2x halves these via
// battleSpeedDivisor; 'instant' bypasses timers entirely (see below).
const BASE_STEP_MS = 250;
const BASE_TURN_START_MS = 400;
const BASE_CAST_SKILL_MS = 800;
const BASE_DEFAULT_MS = 600;
const BASE_INITIAL_MS = 250;
const BASE_FINAL_MS = 250;
const BASE_STEP_SETTLE_MS = 100;

export function useBattleAnimation(
  setBattle: SetBattle,
  addEnhancedBattleLog: (type: string, data: Record<string, unknown>) => void,
  addToBattleLog: (message: string) => void,
  addNotification: (notification: Omit<Notification, 'id'>) => void,
  addFloatingText?: AddFloatingText,
  spawnFx?: SpawnFx,
  triggerHitReaction?: (unitId: number) => void,
  triggerLunge?: (unitId: number) => void,
  triggerDeathAnimation?: (unit: BattleUnitState) => void,
  triggerScreenShake?: () => void
) {
  const animateUnitMovement = useCallback(
    (unitId: number, path: Array<{ x: number; y: number }>, onComplete: () => void) => {
      if (path.length === 0) { onComplete(); return; }
      const speed = getBattleSpeed();
      const lastStep = path[path.length - 1];

      if (speed === 'instant') {
        setBattle((prevBattle) => {
          if (!prevBattle) return prevBattle;
          return {
            ...prevBattle,
            units: prevBattle.units.map((unit) =>
              unit.id === unitId ? { ...unit, position: lastStep } : unit
            ),
          };
        });
        onComplete();
        return;
      }

      const divisor = battleSpeedDivisor(speed);
      const stepMs = BASE_STEP_MS / divisor;
      path.forEach((step, stepIndex) => {
        setTimeout(() => {
          setBattle((prevBattle) => {
            if (!prevBattle) return prevBattle;
            return {
              ...prevBattle,
              units: prevBattle.units.map((unit) =>
                unit.id === unitId ? { ...unit, position: { x: step.x, y: step.y } } : unit
              ),
            };
          });
          if (stepIndex === path.length - 1) setTimeout(onComplete, BASE_STEP_SETTLE_MS / divisor);
        }, stepIndex * stepMs);
      });
    },
    [setBattle]
  );

  const processAIActions = useCallback(
    (aiActions: AIAction[], onComplete?: () => void) => {
      const speed = getBattleSpeed();
      const instant = speed === 'instant';
      const divisor = battleSpeedDivisor(speed);

      const runAction = (actualAction: AIAction) => {
        addEnhancedBattleLog('debug', {
          message: `Processing AI Action: ${actualAction.type} by ${actualAction.unitName || actualAction.casterName || 'unknown'}`,
        });

        switch (actualAction.type) {
          case 'turnStart':
            addEnhancedBattleLog('turn_start', {
              unitName: actualAction.unitName,
              isPlayerUnit: false,
            });
            setBattle((prevBattle) => {
              if (!prevBattle) return prevBattle;
              const aiUnitIndex = prevBattle.turnOrder.findIndex((u) => u.unitId === actualAction.unitId);
              if (aiUnitIndex !== -1) return { ...prevBattle, currentUnitIndex: aiUnitIndex };
              return prevBattle;
            });
            break;

          case 'move':
            if (actualAction.opportunityAttacks && actualAction.opportunityAttacks.length > 0) {
              actualAction.opportunityAttacks.forEach((oa) => {
                if (oa.type === 'Grapple') {
                  if (oa.grappleSuccess) {
                    addToBattleLog(`💥 ${oa.message}`);
                    addNotification({ type: 'loot', title: 'Grapple Success!', message: `Your unit grappled ${oa.targetName}!` });
                  } else {
                    addToBattleLog(`🏃 ${oa.message}`);
                  }
                } else {
                  addToBattleLog(`⚔️ ${oa.message}`);
                  if ((oa.damage ?? 0) > 0) {
                    addNotification({ type: 'loot', title: 'Opportunity Attack', message: `${oa.damage ?? 0} damage to ${oa.targetName}!` });
                  }
                }
              });
            }
            if (actualAction.grappled) {
              addToBattleLog(`🛑 ${actualAction.unitName}'s movement was stopped by grapple!`);
            }
            if (actualAction.path && actualAction.path.length > 0) {
              addEnhancedBattleLog('unit_move', { unitName: actualAction.unitName, distance: actualAction.path.length });
              animateUnitMovement(actualAction.unitId ?? 0, actualAction.path!, () => {});
            }
            break;

          case 'attack': {
            addEnhancedBattleLog('attack', {
              attackerName: actualAction.attackerName,
              targetName: actualAction.targetName,
              damage: actualAction.damage,
              targetDefeated: actualAction.targetDefeated,
              targetRemainingHP: actualAction.targetDefeated ? 0 : (actualAction.targetRemainingHP || 50),
              targetMaxHP: actualAction.targetMaxHP || 100,
              comboDescription: actualAction.comboDescription,
            });

            // Find attacker + target positions from current battle state
            setBattle((prevBattle) => {
              if (!prevBattle || !addFloatingText || !spawnFx) return prevBattle;
              const attacker = prevBattle.units.find(
                (u) => u.name === actualAction.attackerName || u.id === actualAction.unitId
              );
              const target = prevBattle.units.find(
                (u) => u.name === actualAction.targetName
              );
              if (target) {
                const targetPixel = gridToPixel(target.position.x, target.position.y);
                const dist = attacker
                  ? Math.abs(attacker.position.x - target.position.x) + Math.abs(attacker.position.y - target.position.y)
                  : 1;
                if (actualAction.comboType) {
                  addFloatingText(getComboCallout(actualAction.comboType), 'combo', targetPixel.x, targetPixel.y - 30);
                }
                if (attacker && dist <= 1) triggerLunge?.(attacker.id);
                // Damage float on target tile
                if ((actualAction.damage ?? 0) > 0) {
                  addFloatingText(`-${actualAction.damage}`, 'damage', targetPixel.x, targetPixel.y);
                  playSfx(dist > 1 ? 'ranged' : 'hit');
                  triggerHitReaction?.(target.id);
                }
                // FX: melee = slash at target; if attacker far away, projectile
                if (attacker) {
                  spawnFx(dist > 1 ? 'arrow' : 'slash', attacker.position, target.position);
                }
              }
              if (actualAction.targetDefeated) {
                if (target) {
                  const targetPixel = gridToPixel(target.position.x, target.position.y);
                  addFloatingText('💀 Defeated', 'death', targetPixel.x, targetPixel.y);
                  triggerDeathAnimation?.(target);
                }
                playSfx('death');
                triggerScreenShake?.();
              }
              return prevBattle; // state unchanged — we only read it
            });
            break;
          }

          case 'cast_skill': {
            const affectedCount = actualAction.affectedUnits ? actualAction.affectedUnits.length : 1;
            const targetName = affectedCount === 1
              ? (actualAction.affectedUnitNames?.[0] ?? '')
              : '';
            addEnhancedBattleLog('skill_cast', {
              casterName: actualAction.casterName,
              skillName: actualAction.skillName,
              damage: actualAction.damage,
              healing: actualAction.healing,
              affectedCount,
              targetName,
            });

            playSfx('skillCast');
            // Damage/heal floats and FX for each affected unit
            setBattle((prevBattle) => {
              if (!prevBattle || !addFloatingText || !spawnFx) return prevBattle;
              const caster = prevBattle.units.find(
                (u) => u.name === actualAction.casterName || u.id === actualAction.unitId
              );
              (actualAction.affectedUnits ?? []).forEach((affectedId: number) => {
                const target = prevBattle.units.find((u) => u.id === affectedId);
                if (!target) return;
                const targetPixel = gridToPixel(target.position.x, target.position.y);
                if ((actualAction.damage ?? 0) > 0) {
                  addFloatingText(`-${actualAction.damage}`, 'damage', targetPixel.x, targetPixel.y);
                  triggerHitReaction?.(target.id);
                }
                if ((actualAction.healing ?? 0) > 0) {
                  addFloatingText(`+${actualAction.healing}`, 'healing', targetPixel.x, targetPixel.y);
                }
                if (caster) {
                  spawnFx(actualAction.skillName ?? 'magic', caster.position, target.position);
                }
              });
              return prevBattle;
            });
            break;
          }

          case 'phase_transition':
          case 'telegraph':
            if (actualAction.reasoning) addToBattleLog(actualAction.reasoning);
            break;

          case 'summon_minions':
            if (actualAction.reasoning) addToBattleLog(actualAction.reasoning);
            (actualAction.summonedNames ?? []).forEach((name) => addToBattleLog(`👹 ${name} joins the fight!`));
            break;

          case 'battleEnd':
            if (actualAction.result === 'defeat') {
              addEnhancedBattleLog('defeat', {});
            } else if (actualAction.result === 'victory') {
              addEnhancedBattleLog('victory', {});
              if ((actualAction.materialsAwarded?.length ?? 0) > 0)
                addEnhancedBattleLog('materials_found', { materials: actualAction.materialsAwarded });
              if ((actualAction.recipesDiscovered?.length ?? 0) > 0)
                addEnhancedBattleLog('recipes_discovered', { recipes: actualAction.recipesDiscovered });
            }
            break;

          default:
            if (actualAction.damage && actualAction.attackerName && actualAction.targetName) {
              addEnhancedBattleLog('attack', {
                attackerName: actualAction.attackerName,
                targetName: actualAction.targetName,
                damage: actualAction.damage,
                targetDefeated: actualAction.targetDefeated || false,
                targetRemainingHP: actualAction.targetRemainingHP || 50,
                targetMaxHP: actualAction.targetMaxHP || 100,
              });
            }
            break;
        }
      };

      let totalDelay = instant ? 0 : BASE_INITIAL_MS / divisor;

      aiActions.forEach((action) => {
        let actualAction = action;
        if (Array.isArray(action) && action.length > 0) actualAction = action[0];

        if (instant) {
          runAction(actualAction);
        } else {
          setTimeout(() => runAction(actualAction), totalDelay);
        }

        if (actualAction.type === 'turnStart') {
          totalDelay += BASE_TURN_START_MS / divisor;
        } else if (actualAction.type === 'move' && actualAction.path) {
          totalDelay += (actualAction.path.length * BASE_STEP_MS + BASE_TURN_START_MS) / divisor;
        } else if (actualAction.type === 'cast_skill') {
          totalDelay += BASE_CAST_SKILL_MS / divisor;
        } else {
          totalDelay += BASE_DEFAULT_MS / divisor;
        }
      });

      if (onComplete) {
        if (instant) onComplete();
        else setTimeout(onComplete, totalDelay + BASE_FINAL_MS / divisor);
      }
    },
    // addToBattleLog/addEnhancedBattleLog/addFloatingText/spawnFx use functional updaters and are
    // safe as stale closures. animateUnitMovement is a stable useCallback dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setBattle, animateUnitMovement]
  );

  return { processAIActions, animateUnitMovement };
}
